import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
// @ts-ignore
import {useTable, Column} from "react-table";
import * as actions from '../../actions/priceCatalogActions';
import * as columnFormatters from '../../../_common/column-formatters';
import {usePriceCatalogContext} from '../../contexts/PriceCatalogContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid';
import CommonGridSpinner from '../../../_common/components/CommonGridSpinner';
import SortingColumnHeader from "../../../_common/components/SortingColumnHeader/SortingColumnHeader";

type PriceCatalogItem = {
  id?: string;
  mainCategory: string;
  secondCategory: string;
  serviceDescription: string;
  servicePrice: number;
  notes: string;
};

type PriceCatalogRow = {
  id?: React.ReactNode;
  mainCategory: React.ReactNode;
  secondCategory: React.ReactNode;
  serviceDescription: React.ReactNode;
  servicePrice: React.ReactNode;
  notes: React.ReactNode;
  actions: React.ReactNode;
};

export function PriceCatalogTable() {
  const {formatMessage} = useIntl();
  const [tableRows, setTableRows] = useState<PriceCatalogItem[]>([]);

  const prefix = 'price-quote-details-popup-price-catalog-column';

  const columns: Column<PriceCatalogRow>[] = useMemo(() => [
      {
        accessor: 'id',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-id`, defaultMessage: 'ID'})}
            fieldName={'id'}
            useContext={usePriceCatalogContext}
            showDefaultIcon={true}
          />
      },
      {
        accessor: 'mainCategory',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-mainCategory`, defaultMessage: 'Main category'})}
            fieldName={'mainCategory'}
            useContext={usePriceCatalogContext}
          />
      },
      {
        accessor: 'secondCategory',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-secondCategory`, defaultMessage: 'Second category'})}
            fieldName={'secondCategory'}
            useContext={usePriceCatalogContext}
          />
      },
      {
        accessor: 'serviceDescription',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-serviceDescription`, defaultMessage: 'Service description'})}
            fieldName={'serviceDescription'}
            useContext={usePriceCatalogContext}
          />,
        width: 600
      },
      {
        accessor: 'servicePrice',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-servicePrice`, defaultMessage: 'Service price'})}
            fieldName={'servicePrice'}
            useContext={usePriceCatalogContext}
          />
      },
      {
        accessor: 'notes',
        Header:
          <SortingColumnHeader
            text={formatMessage({id: `${prefix}-notes`, defaultMessage: 'Notes'})}
            fieldName={'notes'}
            useContext={usePriceCatalogContext}
          />
      },
      {
        accessor: 'actions',
        Header: '',
        width: 90,
      },
    ].filter(Boolean) as Column<PriceCatalogRow>[],
    []
  );

  const data: PriceCatalogRow[] = useMemo(
    () =>
      tableRows.map((item) => ({
        id: columnFormatters.BoldColumnFormatter(item.id, 'xl'),
        mainCategory: columnFormatters.BoldColumnFormatter(item.mainCategory),
        secondCategory: columnFormatters.BoldColumnFormatter(item.secondCategory),
        serviceDescription: columnFormatters.BoldColumnFormatter(item.serviceDescription),
        servicePrice: columnFormatters.PriceColumnFormatter(item.servicePrice),
        notes: columnFormatters.BoldColumnFormatter(item.notes),
        actions: columnFormatters.PriceCatalogActionsColumnFormatter(item),
      })),
    [tableRows]
  );

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'priceCatalogTableReducer',
    UIContext: usePriceCatalogContext(),
    getNewEntities: actions.getPriceCatalogItems,
    getNextEntities: actions.getNextPriceCatalogItems,
    onEntitiesChange: (entities?: PriceCatalogItem[]) => {
      setTableRows(entities ?? []);
    },
    startRequestedPageNumber: 0,
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: <CommonGridSpinner/>,
    style: {
      overflow: 'inherit',
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      {...useTable<PriceCatalogRow>({columns, data})}
    />
  );
}

export default PriceCatalogTable;